import { memo, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Language } from '../../../hooks/language/use-translate'
import { useTranslate } from '../../../hooks/language/use-translate'
import { gameGoldMinerService } from '../../../services/games/games-golds-miners'
import closeSvg from '../modal-games/components/images/close.svg'

interface Props {
  onClose: () => void
}

type Period = 'daily' | 'weekly' | 'monthly'

const TABS: { key: Period; label: string }[] = [
  { key: 'daily', label: 'Diário' },
  { key: 'weekly', label: 'Semanal' },
  { key: 'monthly', label: 'Mensal' },
]

// intervalo [início, agora] pro período
function rangeFor(period: Period) {
  const now = new Date()
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  if (period === 'weekly') {
    start.setDate(start.getDate() - start.getDay()) // domingo desta semana
  } else if (period === 'monthly') {
    start.setDate(1)
  }
  return {
    createdAtInitial: start.toISOString(),
    createdAtFinal: now.toISOString(),
  }
}

export const ModalRanking = memo(({ onClose }: Props) => {
  const { translate } = useTranslate()
  const title = translate('modal-game-gold-miner-ranking' as Language)
  const [period, setPeriod] = useState<Period>('daily')

  const range = useMemo(() => rangeFor(period), [period])

  const { data: ranking = [], isLoading } = useQuery({
    queryKey: ['gold-miner-ranking', period],
    queryFn: () => gameGoldMinerService.getRanking({ ...range, take: 50 }),
  })

  return (
    <div className="absolute inset-0 z-30 flex flex-col overflow-hidden rounded-t-2xl bg-gradient-to-b from-[#241706] to-black">
      {/* header */}
      <div className="relative flex items-center justify-center px-4 pt-4 pb-2">
        <h2 className="text-lg font-extrabold tracking-wide text-amber-300 drop-shadow">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-1/2 -translate-y-1/2"
        >
          <img src={closeSvg} alt="Fechar" className="h-4 w-4" />
        </button>
      </div>

      {/* abas diário / semanal / mensal */}
      <div className="mx-4 mb-2 flex rounded-full bg-white/5 p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setPeriod(t.key)}
            className={`flex-1 rounded-full py-1.5 text-xs font-bold transition ${
              period === t.key ? 'bg-amber-400 text-black' : 'text-white/60'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* lista */}
      <div className="flex-1 overflow-y-auto px-3 pb-6">
        {isLoading ? (
          <p className="mt-10 text-center text-sm text-white/50">…</p>
        ) : ranking.length === 0 ? (
          <p className="mt-10 text-center text-sm text-white/50">
            Sem ganhadores no período.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {ranking.map((row, i) => (
              <li
                key={row.userId}
                className="flex items-center gap-3 rounded-xl border border-amber-400/10 bg-white/5 px-3 py-2"
              >
                <span
                  className={`w-5 text-center text-sm font-extrabold ${
                    i === 0
                      ? 'text-amber-300'
                      : i === 1
                        ? 'text-gray-300'
                        : i === 2
                          ? 'text-orange-400'
                          : 'text-white/40'
                  }`}
                >
                  {i + 1}
                </span>
                {row.imgUser ? (
                  <img
                    src={row.imgUser}
                    alt={row.userName}
                    className="h-9 w-9 rounded-full object-cover ring-1 ring-amber-400/30"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-amber-400/20" />
                )}
                <span className="flex-1 truncate text-sm text-white/85">
                  {row.userName || `#${row.userId}`}
                </span>
                <span className="text-sm font-bold text-amber-300">
                  {row.total}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
})

ModalRanking.displayName = 'ModalRanking'
